/* eslint-disable import/prefer-default-export */
// @flow
import {fromJS, Record, List, Map} from 'immutable';
import {GuideLevel} from '../GuideLevel/GuideLevel.model';
import {stats} from 'lib/StatsUtil';
import {GenericModel} from 'resources/Generic.model';

const SubjectRecord = Record({
  /* eslint-disable camelcase */
  alternate_name: '',
  code: '',
  description: '',
  domain: '',
  footer_text: '',
  group: '',
  hidden: false,
  id: '',
  logo_image_url: '',
  name: '',
  meta: new Map(),
  free_response_questions_published: false,
  url_slug: '',
  guide_levels: new List(),
  subscriptions: new List(),
  subject_checklist_levels: new List()
  /* eslint-enable camelcase */
});
const SubjectRelationshipConfig = fromJS({
  /* eslint-disable camelcase */
  endpoint: 'subjects_v1',
  relationships: {}
  /* eslint-enable camelcase */
});

export class SubjectModel extends GenericModel(SubjectRecord, SubjectRelationshipConfig) {
  areFRQsPublished(): boolean {
    return this.get('free_response_questions_published', false);
  }

  getAlternateName(): string {
    return this.get('alternate_name');
  }

  getCode(): string {
    return this.get('code');
  }

  getDescription(): string {
    return this.get('description');
  }

  getDomain(): string {
    return this.get('domain');
  }

  getFooterText(): string {
    return this.get('footer_text');
  }

  getGroup(): string {
    return this.get('group');
  }

  isHidden(): boolean {
    return this.get('hidden');
  }

  getId(): string {
    return this.get('id');
  }

  getLogoImageUrl(): string {
    return this.get('logo_image_url');
  }

  getName(): string {
    return this.get('name');
  }

  getUrlSlug(): string {
    return this.get('url_slug');
  }

  getGuideLevels(): List<GuideLevel> {
    return this.get('guide_levels');
  }

  // -----------------------
  // ---- Stats Methods ----
  // -----------------------
  _getAnsweredQuestionsCount(difficulty: string): Map<string, *> {
    return this.getIn(
      ['meta', 'counts', `${difficulty}_questions`],
      new Map({
        correct_answers: 0,
        count: 0,
        questions_answered: 0
      })
    );
  }

  _getAnsweredQuestionsSums(difficulty: string): Map<string, *> {
    return this.getIn(
      ['meta', 'sums', `${difficulty}_questions`],
      new Map({
        points_earned: 0,
        time_elapsed: 0
      })
    );
  }

  _validateDifficulty(difficulty: string) {
    if (difficulty !== 'difficult' && difficulty !== 'medium' && difficulty !== 'easy') {
      throw new Error(
        `Specified difficulty query ${difficulty} is not one of 'easy', 'medium', or 'difficult`
      );
    }
  }

  getCorrectAnswerCountByDifficulty(difficulty: string): number {
    this._validateDifficulty(difficulty);
    return this._getAnsweredQuestionsCount(difficulty).get('correct_answers', 0);
  }

  getQuestionCountByDifficulty(difficulty: string): number {
    this._validateDifficulty(difficulty);
    return this._getAnsweredQuestionsCount(difficulty).get('count', 0);
  }

  getTotalAnsweredCountByDifficulty(difficulty: string): number {
    this._validateDifficulty(difficulty);
    return this._getAnsweredQuestionsCount(difficulty).get('questions_answered', 0);
  }

  getTotalPointsEarnedByDifficulty(difficulty: string): number {
    this._validateDifficulty(difficulty);
    return this._getAnsweredQuestionsSums(difficulty).get('points_earned', 0) || 0;
  }

  getTotalTimeElapsedByDifficulty(difficulty: string): number {
    this._validateDifficulty(difficulty);
    return this._getAnsweredQuestionsSums(difficulty).get('time_elapsed', 0);
  }

  getAverageTimeByDifficulty(difficulty: string): number {
    this._validateDifficulty(difficulty);
    return stats.divide(
      this.getTotalTimeElapsedByDifficulty(difficulty),
      this.getTotalAnsweredCountByDifficulty(difficulty),
      {fixed: false}
    );
  }

  getCompletionPercentageByDifficulty(difficulty: string): number {
    return stats.percentage(
      this.getTotalAnsweredCountByDifficulty(difficulty),
      this.getQuestionCountByDifficulty(difficulty)
    );
  }

  getAverageAccuracyByDifficulty(difficulty: string): number {
    return stats.percentage(
      this.getCorrectAnswerCountByDifficulty(difficulty),
      this.getTotalAnsweredCountByDifficulty(difficulty)
    );
  }

  getTotalPointsEarned(): number {
    return (
      this.getTotalPointsEarnedByDifficulty('difficult') +
      this.getTotalPointsEarnedByDifficulty('medium') +
      this.getTotalPointsEarnedByDifficulty('easy')
    );
  }

  getTotalAnswered(): number {
    return (
      this.getTotalAnsweredCountByDifficulty('difficult') +
      this.getTotalAnsweredCountByDifficulty('medium') +
      this.getTotalAnsweredCountByDifficulty('easy')
    );
  }

  getTotalQuestions(): number {
    return (
      this.getQuestionCountByDifficulty('difficult') +
      this.getQuestionCountByDifficulty('medium') +
      this.getQuestionCountByDifficulty('easy')
    );
  }

  getTotalTimeElapsed(): number {
    return (
      this.getTotalTimeElapsedByDifficulty('difficult') +
      this.getTotalTimeElapsedByDifficulty('medium') +
      this.getTotalTimeElapsedByDifficulty('easy')
    );
  }

  getTotalCorrectAnswers(): number {
    return (
      this.getCorrectAnswerCountByDifficulty('difficult') +
      this.getCorrectAnswerCountByDifficulty('medium') +
      this.getCorrectAnswerCountByDifficulty('easy')
    );
  }

  getAverageAccuracy(): number {
    return stats.percentage(this.getTotalCorrectAnswers(), this.getTotalAnswered());
  }

  getAverageTime(): number {
    return stats.divide(this.getTotalTimeElapsed(), this.getTotalAnswered());
  }

  getCompletionPercentage(): number {
    return stats.percentage(this.getTotalAnswered(), this.getTotalQuestions());
  }
}
